<template>
  <div :class="wrapperClass">
    <slot name="loading" v-if="loading"></slot>
    <table v-else class="data-table w-full">
      <thead>
        <tr>
          <th
            v-for="(col, index) in normalizedColumns"
            :key="index"
            :style="getColumnStyle(col, index)"
            :class="[
              col.headerClass,
              {
                sorted: sortKey === col.field,
                'sortable-column': col.sortable,
              },
            ]"
            class="even-row"
            @click="col.sortable ? handleHeaderClick(col) : null"
          >
            <template v-if="col.selectionMode === 'multiple'">
              <form-checkbox
                :id="'header-checkbox'"
                :modelValue="isAllSelected"
                @update:modelValue="toggleAllSelection"
                :disabled="paginatedData.length === 0"
                :PropName="'Select All'"
              />
            </template>
            <template v-else>
              <span class="header-content">
                {{ col.header }}
                <i
                  v-if="col.sortable"
                  :class="[
                    sortKey === col.field && sortOrder === 'asc'
                      ? sortIcons.asc
                      : sortKey === col.field && sortOrder === 'desc'
                      ? sortIcons.desc
                      : sortIcons.neutral,
                  ]"
                ></i>
              </span>
            </template>
            <span
              v-if="index < normalizedColumns.length - 1"
              class="resize-handle"
              @mousedown.stop="startResize($event, index)"
            ></span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, rowIndex) in paginatedData"
          :key="getRowKey(row)"
          :class="{
            'row-disabled': rowDisabled ? rowDisabled(row) : false,
            'clickable-row': true,
            'even-row': rowIndex % 2 === 0,
            'odd-row': rowIndex % 2 !== 0,
            'selected-row': isRowSelected(row),
          }"
        >
          <td
            v-for="(col, colIndex) in normalizedColumns"
            :key="colIndex"
            :style="getCellStyle(col, colIndex)"
            :class="[
              getCellClass(col),
              { 'text-center': isNumericColumn(col.field) },
            ]"
            @click="handleCellClick(col, row, rowIndex)"
          >
            <template v-if="col.selectionMode === 'multiple'">
              <form-checkbox
                :id="`row-checkbox-${rowIndex}`"
                :modelValue="isRowSelected(row)"
                @update:modelValue="toggleRowSelection(row)"
                :disabled="rowDisabled ? rowDisabled(row) : false"
                :PropName="`Row ${rowIndex + 1}`"
              />
            </template>
            <template v-else-if="col.bodySlot && col.useBodySlot">
              <component :is="col.bodySlot" :data="row" :index="rowIndex" :isColumnResized="resizedColumns[colIndex] || false" />
            </template>
            <template v-else-if="$slots[col.field]">
              <slot :name="col.field" :data="row" :index="rowIndex" :isColumnResized="resizedColumns[colIndex] || false" />
            </template>
            <template v-else-if="col.slotName && $slots[col.slotName]">
              <slot :name="col.slotName" :data="row" :index="rowIndex" :isColumnResized="resizedColumns[colIndex] || false" />
            </template>
            <template v-else>
              <span v-if="col.linkable" class="atlink">
                {{ row[col.field] }}
              </span>
              <span v-else-if="col.field === 'actions'">
                <button
                  class="action-button"
                  @click.stop="toggleActionsDropdown(row, $event)"
                >
                  <i class="icon-shared"></i>
                </button>
                <teleport to="body">
                  <div
                    v-if="isActionsOpen && currentRow === row"
                    ref="dropdownMenu"
                    :style="{
                      top: dropdownTop + 'px',
                      left: dropdownLeft + 'px',
                    }"
                    class="dropdown-menu"
                  >
                    <div
                      v-for="(option, index) in getVisibleActions(
                        row,
                        splitValues
                      )"
                      :key="index"
                      class="dropdown-item"
                      @click.stop="handleAction(option.action, row)"
                    >
                      <i
                        v-if="option.icon"
                        :class="
                          typeof option.icon === 'function'
                            ? option.icon(row)
                            : option.icon
                        "
                        style="margin-right: 8px"
                      ></i>
                      {{
                        typeof option.label === "function"
                          ? option.label(row)
                          : option.label
                      }}
                    </div>
                  </div>
                </teleport>
              </span>
              <span v-else-if="col.field === 'payload' && row.payload !== null">
                <i class="fa fa-eye"></i>
              </span>
              <span v-else>
                {{ row[col.field] }}
              </span>
            </template>
          </td>
        </tr>
        <tr v-if="!paginatedData || paginatedData.length === 0">
          <td :colspan="normalizedColumns.length" class="no-data">
            <slot name="empty">No data available.</slot>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="paginator" v-if="totalPages > 1 && isPagination == true">
      <button
        :disabled="currentPage === 1"
        class="paginator-button paginator-first"
        @click="changePage(1)"
        aria-label="First Page"
      >
        <span class="icon pagination-first-icon"></span>
      </button>
      <button
        :disabled="currentPage === 1"
        class="paginator-button paginator-prev"
        @click="changePage(currentPage - 1)"
        aria-label="Previous Page"
      >
        <span class="icon pagination-prev-icon"></span>
      </button>
      <div class="paginator-pages">
        <button
          v-for="page in pageLinks"
          :key="page"
          :class="[
            'paginator-page',
            { 'paginator-page-active': page === currentPage },
          ]"
          @click="changePage(page)"
          :aria-label="`Page ${page}`"
        >
          {{ page }}
        </button>
      </div>
      <button
        :disabled="currentPage === totalPages"
        class="paginator-button paginator-next"
        @click="changePage(currentPage + 1)"
        aria-label="Next Page"
      >
        <span class="icon pagination-next-icon"></span>
      </button>
      <button
        :disabled="currentPage === totalPages"
        class="paginator-button paginator-last"
        @click="changePage(totalPages)"
        aria-label="Last Page"
      >
        <span class="icon pagination-last-icon"></span>
      </button>
    </div>

    <span
      v-if="validation?.$error && validation?.required?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ PropName }} is required.
    </span>
  </div>
</template>

<script>
export default {
  name: "DataTableVue",
  props: {
    id: { type: String, required: false, default: "" },
    value: { type: Array, required: true },
    selection: { type: [Array, Object], default: null },
    dataKey: { type: String, default: null },
    wrapperClass: { type: String, default: "table-wrapper" },
    rowDisabled: { type: Function, default: null },
    validation: { type: Object, default: null },
    PropName: { type: String, default: "" },
    isSort: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    isPagination: { type: Boolean, default: false },
    rowClickRoute: { type: String, default: null },
    splitValues: { type: Array, default: () => [] },
    sortIcons: {
      type: Object,
      default: () => ({
        asc: "sorting-icon",
        desc: "sorting-icon",
        neutral: "sorting-icon",
      }),
    },
    perPage: { type: Number, default: 10 },
    pageLinkSize: { type: Number, default: 5 },
  },
  emits: [ 
    "update:selection",
    "page",
    "sort",
    "row-click",
    "action-click",
    "cell-click",
    "handleSplitValue",
  ],
  data() {
    return {
      selectedRows: this.selection || [],
      sortKey: null,
      sortOrder: "asc",
      isActionsOpen: false,
      currentRow: null,
      currentPage: 1,
      dropdownTop: 0,
      dropdownLeft: 0,
      columnWidths: [],
      resizedColumns: [],
      minColumnWidths: [],
      isResizing: false,
      resizingColumnIndex: null,
      startX: 0,
      startWidth: 0,
      resizeJustEnded: false,
    };
  },
  computed: {
    normalizedColumns() {
      const cols =
        this.$slots
          .default?.()
          .filter((vnode) => vnode.type?.name === "Column")
          .map((vnode) => {
            const props = vnode.props || {};
            return {
              field: props.field,
              header: props.header,
              sortable: props.sortable,
              selectionMode: props.selectionMode,
              headerStyle: props.headerStyle,
              headerClass: props.headerClass,
              slotName: props.field,
              style: props.style,
              class: props.class,
              cellClass: props.cellClass,
              onCellClick: props.onCellClick,
              hasRowClick: !!props.onRowClick,
              linkable: props.linkable || false,
              bodySlot: vnode.children?.body || null,
              useBodySlot: props.useBodySlot || false,
              minWidth: props.minWidth || props['min-width'] ? Number(props.minWidth || props['min-width']) : null,
            };
          }) || [];
      return cols;
    },
    isAllSelected() {
      return (
        this.paginatedData.length > 0 &&
        this.paginatedData.every((row) => this.isRowSelected(row))
      );
    },
    totalRecords() {
      return this.value.length;
    },
    totalPages() {
      return Math.ceil(this.totalRecords / this.perPage) || 1;
    },
    paginatedData() {
      if (!this.isPagination) {
        return this.value;
      }
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.value.slice(start, end);
    },
    pageLinks() {
      const links = [];
      const totalPages = this.totalPages;
      const currentPage = this.currentPage;
      const pageLinkSize = Math.min(this.pageLinkSize, totalPages);

      let startPage = Math.max(1, currentPage - Math.floor(pageLinkSize / 2));
      let endPage = startPage + pageLinkSize - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - pageLinkSize + 1);
      }

      startPage = Math.max(1, startPage);

      for (let i = startPage; i <= endPage; i++) {
        links.push(i);
      }

      return links;
    },
  },
  watch: {
    selectedRows(newSelection) {
      this.$emit("update:selection", newSelection);
    },
    selection(newSelection) {
      this.selectedRows = newSelection || [];
    },
    value() {
      this.currentPage = 1;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("mousemove", this.onResize);
    document.addEventListener("mouseup", this.stopResize);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("mousemove", this.onResize);
    document.removeEventListener("mouseup", this.stopResize);
  },
  methods: {
    isRowSelected(row) {
      if (!this.dataKey) {
        return this.selectedRows.includes(row);
      }
      return this.selectedRows.some(
        (selected) => selected[this.dataKey] === row[this.dataKey]
      );
    },
    handleOutsideClick(event) {
      const dropdown = this.$refs.dropdownMenu;
      const actionButton = this.$el.querySelector(".action-button");
      if (this.isActionsOpen && dropdown && actionButton) {
        const isOutside = !event
          .composedPath()
          .some((el) => el === dropdown || el === actionButton);
        if (isOutside) {
          this.isActionsOpen = false;
          this.currentRow = null;
        }
      }
    },
    getRowKey(row) {
      return this.dataKey ? row[this.dataKey] : row;
    },
    getCellClass(column) {
      return column.cellClass || "";
    },
    isNumericColumn(field) {
      return ["users", "sites", "deals"].includes(field);
    },
    handleCellClick(column, row, rowIndex) {
      if (column.onCellClick) {
        if (typeof column.onCellClick === "function") {
          column.onCellClick({ row, rowIndex });
        } else {
          this.$emit("cell-click", { column, row, rowIndex });
        }
        return;
      }
      if (column.hasRowClick && this.rowClickRoute && row.companyId) {
        try {
          this.$router.push({
            name: this.rowClickRoute,
            params: { id: row.companyId },
          });
          this.$emit("row-click", { row, rowIndex });
        } catch (error) {
          console.error("Navigation error:", error);
        }
      }
    },
    toggleAllSelection(checked) {
      this.selectedRows = checked ? [...this.paginatedData] : [];
      this.$emit("update:selection", this.selectedRows);
    },
    toggleRowSelection(row) {
      if (this.isRowSelected(row)) {
        if (this.dataKey) {
          this.selectedRows = this.selectedRows.filter(
            (selected) => selected[this.dataKey] !== row[this.dataKey]
          );
        } else {
          this.selectedRows = this.selectedRows.filter(
            (selected) => selected !== row
          );
        }
      } else {
        this.selectedRows = [...this.selectedRows, row];
      }
      this.$emit("update:selection", this.selectedRows);
    },
    handleHeaderClick(col) {
      if (this.resizeJustEnded) {
        this.resizeJustEnded = false;
        return;
      }
      this.sortColumn(col);
    },
    sortColumn(col) {
      if (this.sortKey === col.field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = col.field;
        this.sortOrder = "asc";
      }
      this.$emit("sort", { field: this.sortKey, order: this.sortOrder });
    },
    toggleActionsDropdown(row, event) {
      event.stopPropagation();
      const buttonRect = event.target.getBoundingClientRect();
      this.dropdownLeft = buttonRect.left + window.scrollX - 86;

      // Estimate dropdown height based on number of items
      const dropdownHeight =
        this.getVisibleActions(row, this.splitValues).length * 32 + 10;
      const spaceBelow = window.innerHeight - buttonRect.bottom;

      // Position dropdown above if not enough space below, else below
      this.dropdownTop =
        spaceBelow < dropdownHeight
          ? buttonRect.top + window.scrollY - dropdownHeight - 5
          : buttonRect.bottom + window.scrollY + 5;

      this.isActionsOpen =
        this.isActionsOpen && this.currentRow === row ? false : true;
      this.currentRow = this.isActionsOpen ? row : null;
    },
    handleAction(action, row) {
      if (action) {
        if (typeof action === "function") {
          action(row);
        } else {
          this.$emit("action-click", { row, action });
        }
      }
      this.isActionsOpen = false;
      this.currentRow = null;
    },
    handleSplitValue(action, row) {
      if (action) {
        if (typeof action === "function") {
          action(row);
        } else {
          this.$emit("action-click", { row, action });
        }
      }
      this.isActionsOpen = false;
      this.currentRow = null;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.$emit("page", {
          page: page - 1,
          rows: this.perPage,
          first: (page - 1) * this.perPage,
        });
      }
    },
    getColumnStyle(col, index) {
      const width = this.columnWidths[index];
      let style = {};
      
      // Safely merge headerStyle if it exists and is an object
      if (col.headerStyle && typeof col.headerStyle === 'object' && !Array.isArray(col.headerStyle)) {
        style = { ...col.headerStyle };
      }
      
      // Apply column width if it exists
      if (width) {
        style.width = `${width}px`;
        style.minWidth = `${width}px`;
        style.maxWidth = `${width}px`;
      }
      
      return style;
    },
    getCellStyle(col, index) {
      const width = this.columnWidths[index];
      let style = {};
      
      // Apply column width if it exists
      if (width) {
        style.width = `${width}px`;
        style.minWidth = `${width}px`;
        style.maxWidth = `${width}px`;
      }
      
      // Apply word-break and overflow-wrap only if this column has been resized
      if (this.resizedColumns[index]) {
        style.wordBreak = 'break-word';
        style.overflowWrap = 'break-word';
        style.whiteSpace = 'normal';
      }
      
      return style;
    },
    startResize(event, columnIndex) {
      this.isResizing = true;
      this.resizingColumnIndex = columnIndex;
      this.startX = event.pageX;
      const th = event.target.parentElement;
      this.startWidth = th.offsetWidth;
      
      // Cache header cells for direct manipulation
      this.resizingTh = th;
      const allRows = this.$el.querySelectorAll('tbody tr');
      this.resizingTds = Array.from(allRows).map(row => row.children[columnIndex]);
      
      if (!this.columnWidths[columnIndex]) {
        this.columnWidths[columnIndex] = this.startWidth;
      }
      
      // Set minimum width based on:
      // 1. If column has minWidth prop, use that value
      // 2. Otherwise, if initial width > 100px, min is 110px
      // 3. Otherwise, min is the initial width
      if (this.minColumnWidths[columnIndex] === undefined) {
        const column = this.normalizedColumns[columnIndex];
        if (column && column.minWidth !== null) {
          // Use column-specific minWidth prop
          this.minColumnWidths[columnIndex] = column.minWidth;
        } else {
          // Default logic: > 100px initial = 110px min, else initial width
          this.minColumnWidths[columnIndex] = this.startWidth > 100 ? 110 : this.startWidth;
        }
      }
      
      // Mark this column as resized
      this.resizedColumns[columnIndex] = true;
      event.preventDefault();
    },
    onResize(event) {
      if (!this.isResizing) return;
      const diff = event.pageX - this.startX;
      const minWidth = this.minColumnWidths[this.resizingColumnIndex] || 50;
      const newWidth = Math.max(minWidth, this.startWidth + diff);
      const widthPx = `${newWidth}px`;
      
      // Direct DOM manipulation for instant visual feedback
      if (this.resizingTh) {
        this.resizingTh.style.width = widthPx;
        this.resizingTh.style.minWidth = widthPx;
        this.resizingTh.style.maxWidth = widthPx;
      }
      
      // Update all cells in the column
      if (this.resizingTds) {
        this.resizingTds.forEach(td => {
          if (td) {
            td.style.width = widthPx;
            td.style.minWidth = widthPx;
            td.style.maxWidth = widthPx;
          }
        });
      }
      
      // Update reactive state
      this.columnWidths[this.resizingColumnIndex] = newWidth;
    },
    stopResize() {
      if (this.isResizing) {
        this.resizeJustEnded = true;
        setTimeout(() => {
          this.resizeJustEnded = false;
        }, 100);
      }
      this.isResizing = false;
      this.resizingColumnIndex = null;
      this.resizingTh = null;
      this.resizingTds = null;
    },
    getVisibleActions(row, actions) {
      return actions.filter(
        (option) =>
          !option.visible ||
          (typeof option.visible === "function"
            ? option.visible(row)
            : option.visible)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
}
.data-table {
  border-collapse: collapse;
  font-family: "Voyado Text";
  color: #212529;
  width: 100%;
}
th {
  background-color: #ffffff;
  border-bottom: 1px solid #e2e2e2;
  text-align: left;
  position: sticky;
  font-weight: 600;
  color: #212529;
  overflow-wrap: break-word;
  padding: 10px;
  position: relative;
}
td {
  border-bottom: 1px solid #e2e2e2;
  font-size: 14.22px;
  text-align: left;
  color: #212529;
  padding: 10px;
  box-sizing: border-box;
}
.even-row {
  background-color: #fff;
}
.odd-row {
  background-color: #fff;
}
tr:hover {
  background-color: #fff;
}
.clickable-row {
  cursor: pointer;
}
.row-disabled {
  td {
    opacity: 0.6;
    pointer-events: none;
  }
}
.no-data {
  text-align: center;
  padding: 20px;
  color: #666;
}
.p-error {
  font-size: 14px;
  color: #dc3545;
}
.text-center {
  text-align: center;
}
.atlink {
  color: #388988;
  text-decoration: underline;
  cursor: pointer;
}
.atlink:hover {
  color: #388988;
  text-decoration: underline;
}
.actions-cell {
  text-align: center;
}
.actions-cell i {
  cursor: pointer;
  font-size: 16px;
  color: #666;
}
.actions-cell i:hover {
  color: #2d6e6d;
}
.action-button {
  height: 40px;
  padding: 16px;
  gap: 8px;
  border-radius: 4px;
  border: 2px solid var(--forest-100, #e0e9e8);
  background: #fff;
  cursor: pointer;
  font-size: 14.22px;
  font-family: "Voyado Text", sans-serif;
}
.dropdown-menu {
  position: absolute;
  background-color: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 137px;
  width: 160px;
  padding: 5px 0;
}
.dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  color: #333;
  display: flex;
}
.dropdown-item:hover {
  background-color: #f5f5f5;
}
.header-content {
  display: flex;
  align-items: center;
  font-size: 13px;
  justify-content: space-between;
}
::deep(.sort-icon) {
  margin-left: 0;
  vertical-align: middle;
}
.paginator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  font-family: "Voyado Text", sans-serif;
  font-size: 14px;
  color: #2d6e6d;
  margin-top: 16px;
}
.paginator-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 0.6px solid #e0e0e0;
  background: #ffffff;
  cursor: pointer;
  color: #2d6e6d;
  transition: background-color 0.2s, border-color 0.2s;
}
.paginator-button:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #dee2e6;
  color: #2d6e6d;
  box-shadow: none;
}
.paginator-button:enabled:focus {
  outline-offset: 0;
  border-style: double;
  box-shadow: none;
}
.paginator-button:disabled {
  opacity: 0.5;
  cursor: default;
}
.paginator-pages {
  display: flex;
}
.paginator-page {
  justify-content: center;
  min-width: 2.5rem;
  height: 2.5rem;
  border: 0.6px solid #e0e0e0;
  background: #ffffff;
  cursor: pointer;
  color: #2d6e6d;
  padding: 0 0.5rem;
  gap: 2px;
  font-weight: 500;
}
.paginator-page:hover {
  background: #e9ecef;
  border-color: #dee2e6;
  color: #2d6e6d;
  box-shadow: none;
}
.paginator-page-active {
  background: #2d6e6d;
  color: #ffffff;
  font-weight: 500;
}
.paginator-page-active:hover {
  background: #2d6e6d;
  color: #ffffff;
  font-weight: 500;
}
.icon {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  background-size: contain;
  background-repeat: no-repeat;
}
.selected-row {
  background-color: #edf8f4;
}
.selected-row:hover {
  background-color: #edf8f4;
}
.sortable-column {
  cursor: pointer;
  .header-content i {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }
  &:hover {
    .header-content i {
      opacity: 1;
    }
  }
  &.sorted {
    .header-content i {
      opacity: 1;
    }
  }
}
th:not(.sortable-column) {
  cursor: default;
}
th:not(.sortable-column) .header-content i {
  display: none;
}
td:last-child {
  position: relative;
}
.resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  z-index: 1;
}
.resize-handle:hover {
  background-color: #e0e9e8;
}
</style>
